import React from "react";
import { Survey, SurveySegment, SegmentReport, PublicSurveyReportLink, PortalSharedSurveyReportView, SurveySegmentFilterAlias, stringUtil, rng, segmentListsOfListsMatch } from "../../../../../../../amorg-common";
import { portalClientApi } from "../../../../../../../api/portalClientApi";
import { getSurveyReportLinks, getSurveySegmentFilterAliases, createSurveySegmentFilterAlias } from "../../../../../../../api/surveyClientApi";
import { Button } from "../../../../../../../designSystem/button/Button";
import { Card } from "../../../../../../../designSystem/Card";
import { Flex } from "../../../../../../../designSystem/compositeComponents/FlexHelpers";
import { Modal } from "../../../../../../../designSystem/Modal";
import { amorgLocalStorage } from "../../../../../../../utils/amorgLocalStorage";
import { SurveyReportViewList } from "./SurveyReportViewList";
import { Label } from "../../../../../../../designSystem/Label";
import { EditableSegmentPanel } from "../../../../../../_components/activities/EditableSegmentPanel";


type SurveyReportViewPanelProps = { siteId: string, survey:Survey, segmentFilter: SurveySegment[][], segmentReports: SegmentReport[], onFilterChanged: (filter: SurveySegment[][]) => void };
type PageState = {
    segmentLists: {
        segments: SurveySegment[],
        answerCount: number;
    }[],
    surveyLinks: PublicSurveyReportLink[],
    portalReports: PortalSharedSurveyReportView[],
    aliases: SurveySegmentFilterAlias[],
    loading: boolean,
    modalError?: string
};
export function SurveyReportViewPanel (props: SurveyReportViewPanelProps): JSX.Element
{
    const [state, setState] = React.useState<PageState>({ 
        surveyLinks: [], 
        portalReports: [], 
        aliases: [],
        segmentLists: [],
        loading: true,
    });

    function getData(surveyId: string): Promise<{ surveyLinks: PublicSurveyReportLink[], portalReports: PortalSharedSurveyReportView[], aliases: SurveySegmentFilterAlias[] }>
    {
        return Promise.all([
            getSurveyReportLinks(surveyId),
            portalClientApi.getPortalSurveyReports(surveyId),
            getSurveySegmentFilterAliases(surveyId)
        ]).then(([surveyLinks, portalReports, aliases]) => ({ surveyLinks, portalReports, aliases }));
    }

    // Only run this once - requires refreshing when the component itself updates data
    React.useEffect(() =>
    {
        async function get ()
        {
            const data = await getData(props.survey.id);
            setState({ ...data, segmentLists: state.segmentLists, loading: false });
        }
        get();
    }, [props.survey.id]);

    async function handleChange()
    {
        setState({ ...state, loading: true });
        const data = await getData(props.survey.id);
        setState({ ...state, ...data, loading: false });
    }

    async function handleSelectView (segmentFilter: SurveySegment[][])
    {
        props.onFilterChanged(segmentFilter);
    }

    async function handleSaveSelection()
    {
        setState({ ...state, loading: true });

        const newName = stringUtil.generateNextSequenceValue('Ny visning', state.aliases.map(a => a.name));

        const result = await createSurveySegmentFilterAlias(
            props.survey.id, 
            rng.getRandomId(),
            newName,
            props.segmentFilter
        );

        if(result.isSuccess)
        {
            const data = await getData(props.survey.id);
            setState({ ...state, ...data, loading: false });
        }
        else {
            if(result.errorMessage === 'createSurveySegmentFilterAlias_invalid_name')
            {
                setState({ ...state, modalError: 'Ugyldigt navn', loading: false });
            }
            else{
                setState({ ...state, modalError: 'Der skete en fejl under oprettelsen af den nye visning', loading: false });
            }
        }
    }

    const canSave = state.surveyLinks.find(l => segmentListsOfListsMatch(l.configuration.segmentFilter, props.segmentFilter)) === undefined
        && state.portalReports.find(r => segmentListsOfListsMatch(r.segmentFilter, props.segmentFilter)) === undefined
        && state.aliases.find(a => segmentListsOfListsMatch(a.segmentFilter, props.segmentFilter)) === undefined;

    return (
        
        <Flex flexDirection="column" gap={16}>

            <Flex flexDirection="column" gap={16}>

                <Modal showModal={!!state.modalError} onCancel={() => setState({ ...state, modalError: undefined })} title="Fejl"
                    content={state.modalError ?? ''} />

                <EditableSegmentPanel
                    displayAnswerCount
                    localStorageViewModeKey={amorgLocalStorage.surveySegmentation.viewMode.getKey('survey-report', props.survey.id)}
                    segmentLists={props.segmentReports}
                    selection={props.segmentFilter}
                    onSelectionChange={props.onFilterChanged}
                />

                <Flex justifyContent="flex-end">
                    <Button variant="outlined" 
                        size="32px"
                        text="Gem som ny visning"
                        disabled={state.loading || !canSave}
                        toolTipText={canSave 
                            ? "Gem den valgte segmentering som en rapportvisning, så den kan deles og genbruges senere"
                            : "Denne visning er allerede gemt"
                        } 
                        onClick={handleSaveSelection} />
                </Flex>
            </Flex>

            <div>
                <Label>
                    Oprettede visninger
                </Label>
                {/* <Divider  /> */}
                <Card variant="outlined" style={{ padding: '8px 8px'}}>

                    <SurveyReportViewList
                        siteId={props.siteId}
                        survey={props.survey}
                        surveyLinks={state.surveyLinks}
                        portalReports={state.portalReports}
                        aliases={state.aliases}
                        segmentReports={props.segmentReports}
                        onChanged={handleChange}
                        onSelectSegmentFilter={handleSelectView}
                        onLoadingChange={(loading) => setState({ ...state, loading })} />

                </Card>
            </div>

        </Flex>
    );
}